import { RegionalSettings } from '@/types/regional';

export class RegionalFormatter {
  constructor(private settings: RegionalSettings) {}

  formatDate(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: this.settings.timezone
    };

    if (this.settings.dateFormat === 'YYYY-MM-DD') {
      return date.toLocaleDateString('sv-SE', options);
    }

    const formatted = date.toLocaleDateString(this.settings.locale, options);
    
    if (this.settings.dateFormat === 'MM/DD/YYYY') {
      return formatted;
    }
    
    // Convert MM/DD/YYYY to DD/MM/YYYY for EU format
    const parts = formatted.split('/');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[0]}/${parts[2]}`;
    }
    
    return formatted;
  }

  formatTime(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: this.settings.timeFormat === '12h',
      timeZone: this.settings.timezone
    };

    return date.toLocaleTimeString(this.settings.locale, options);
  }

  formatDateTime(date: Date): string {
    return `${this.formatDate(date)} ${this.formatTime(date)}`;
  }

  formatCurrency(amount: number): string {
    try {
      return new Intl.NumberFormat(this.settings.locale, {
        style: 'currency',
        currency: this.settings.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      // Fallback for unsupported currencies
      return `${this.settings.currency} ${amount.toFixed(2)}`;
    }
  }

  formatNumber(num: number): string {
    return new Intl.NumberFormat(this.settings.locale).format(num);
  }

  formatFileSize(bytes: number): string {
    // Use binary (1024) for most regions, decimal (1000) for some
    const usesDecimal = ['fr-FR', 'es-ES', 'pt-PT', 'it-IT'].includes(this.settings.locale);
    const base = usesDecimal ? 1000 : 1024;
    const units = usesDecimal ? ['B', 'kB', 'MB', 'GB', 'TB'] : ['B', 'KB', 'MB', 'GB', 'TB'];
    
    let size = bytes;
    let unitIndex = 0;

    while (size >= base && unitIndex < units.length - 1) {
      size /= base;
      unitIndex++;
    }

    const formatted = this.formatNumber(Math.round(size * 100) / 100);
    return `${formatted} ${units[unitIndex]}`;
  }

  getTimezone(): string {
    return this.settings.timezone;
  }

  getUserTimezone(): string {
    return this.settings.timezone;
  }

  convertToUserTimezone(date: Date): Date {
    // Handle DST transitions properly
    try {
      const utcTime = date.getTime();
      const targetDate = new Date(utcTime);
      
      // Use Intl.DateTimeFormat to handle DST correctly
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: this.settings.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const parts = formatter.formatToParts(targetDate);
      const year = parseInt(parts.find(p => p.type === 'year')?.value || '0');
      const month = parseInt(parts.find(p => p.type === 'month')?.value || '1') - 1;
      const day = parseInt(parts.find(p => p.type === 'day')?.value || '1');
      const hour = parseInt(parts.find(p => p.type === 'hour')?.value || '0');
      const minute = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
      const second = parseInt(parts.find(p => p.type === 'second')?.value || '0');
      
      return new Date(year, month, day, hour, minute, second);
    } catch (error) {
      console.warn('Timezone conversion failed, using original date:', error);
      return date;
    }
  }

  isRTL(): boolean {
    const rtlLanguages = ['ar', 'he', 'fa', 'ur', 'yi'];
    return rtlLanguages.includes(this.settings.language);
  }

  formatPercentage(value: number): string {
    return new Intl.NumberFormat(this.settings.locale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 1
    }).format(value / 100);
  }

  formatPhoneNumber(phone: string): string {
    // Enhanced regional phone formatting
    const cleaned = phone.replace(/\D/g, '');
    
    // Regional phone format patterns
    const patterns: Record<string, (phone: string) => string> = {
      'US': (p) => p.length === 11 && p.startsWith('1') ? `+1 (${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7)}` : p,
      'GB': (p) => p.startsWith('44') ? `+44 ${p.slice(2, 6)} ${p.slice(6)}` : p,
      'DE': (p) => p.startsWith('49') ? `+49 ${p.slice(2, 5)} ${p.slice(5)}` : p,
      'FR': (p) => p.startsWith('33') ? `+33 ${p.slice(2, 3)} ${p.slice(3, 5)} ${p.slice(5, 7)} ${p.slice(7)}` : p,
      'JP': (p) => p.startsWith('81') ? `+81 ${p.slice(2, 4)} ${p.slice(4, 8)} ${p.slice(8)}` : p,
      'CN': (p) => p.startsWith('86') ? `+86 ${p.slice(2, 5)} ${p.slice(5, 9)} ${p.slice(9)}` : p,
      'IN': (p) => p.startsWith('91') ? `+91 ${p.slice(2, 7)} ${p.slice(7)}` : p
    };
    
    const region = this.settings.region || 'US';
    const formatter = patterns[region];
    
    if (formatter) {
      const formatted = formatter(cleaned);
      if (formatted !== cleaned) return formatted;
    }
    
    // Generic international format
    if (cleaned.length > 7) {
      return `+${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
    }
    
    return phone;
  }

  validatePhoneNumber(phone: string): boolean {
    const cleaned = phone.replace(/\D/g, '');
    
    // Regional validation patterns
    const validationPatterns: Record<string, RegExp> = {
      'US': /^1[2-9]\d{9}$/,
      'GB': /^44[1-9]\d{8,9}$/,
      'DE': /^49[1-9]\d{8,11}$/,
      'FR': /^33[1-9]\d{8}$/,
      'JP': /^81[1-9]\d{8,9}$/,
      'CN': /^86[1-9]\d{9,10}$/,
      'IN': /^91[6-9]\d{9}$/
    };
    
    const region = this.settings.region || 'US';
    const pattern = validationPatterns[region];
    
    if (pattern) {
      return pattern.test(cleaned);
    }
    
    // Generic validation: 7-15 digits
    return /^\d{7,15}$/.test(cleaned);
  }

  formatCronExpression(cron: string): string {
    // Convert cron to human readable format considering regional preferences
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;

    const [minute, hour, day, month, weekday] = parts;
    
    if (minute === '0' && hour !== '*') {
      const hourNum = parseInt(hour);
      const time = new Date();
      time.setHours(hourNum, 0, 0, 0);
      return `Daily at ${this.formatTime(time)}`;
    }
    
    if (minute === '0' && hour === '0' && weekday !== '*') {
      const days = this.getLocalizedDayNames();
      return `Weekly on ${days[parseInt(weekday)]}`;
    }
    
    if (minute === '0' && hour === '0' && day === '1') {
      return 'Monthly on the 1st';
    }
    
    return cron;
  }

  validateCronField(field: string, min: number, max: number): boolean {
    if (field === '*') return true;
    
    if (field.includes('/')) {
      const [range, step] = field.split('/');
      const stepNum = parseInt(step);
      if (isNaN(stepNum) || stepNum <= 0) return false;
      if (range === '*') return true;
      return this.validateCronRange(range, min, max);
    }
    
    if (field.includes('-')) {
      return this.validateCronRange(field, min, max);
    }
    
    if (field.includes(',')) {
      return field.split(',').every(part => this.validateCronField(part.trim(), min, max));
    }
    
    const num = parseInt(field);
    return !isNaN(num) && num >= min && num <= max;
  }

  private validateCronRange(range: string, min: number, max: number): boolean {
    const parts = range.split('-');
    if (parts.length !== 2) return false;
    const [start, end] = parts.map(n => parseInt(n));
    return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end;
  }

  validateCronExpression(cron: string): { valid: boolean; error?: string } {
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) {
      return { valid: false, error: 'Cron expression must have 5 fields (minute hour day month weekday)' };
    }

    const [minute, hour, day, month, weekday] = parts;
    
    if (!this.validateCronField(minute, 0, 59)) {
      return { valid: false, error: 'Invalid minute field in cron expression' };
    }
    if (!this.validateCronField(hour, 0, 23)) {
      return { valid: false, error: 'Invalid hour field in cron expression' };
    }
    if (!this.validateCronField(day, 1, 31)) {
      return { valid: false, error: 'Invalid day field in cron expression' };
    }
    if (!this.validateCronField(month, 1, 12)) {
      return { valid: false, error: 'Invalid month field in cron expression' };
    }
    if (!this.validateCronField(weekday, 0, 7)) {
      return { valid: false, error: 'Invalid weekday field in cron expression' };
    }
    
    return { valid: true };
  }

  getLocalizedDayNames(): string[] {
    const days = [];
    const baseDate = new Date(2024, 0, 7); // A Sunday
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      days.push(date.toLocaleDateString(this.settings.locale, { weekday: 'long' }));
    }
    
    // Adjust for first day of week
    if (this.settings.firstDayOfWeek === 1) {
      const sunday = days.shift();
      if (sunday) days.push(sunday);
    }
    
    return days;
  }

  getLocalizedMonthNames(): string[] {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(2024, i, 1);
      months.push(date.toLocaleDateString(this.settings.locale, { month: 'long' }));
    }
    return months;
  }

  // Regional compliance validation
  validateDataResidency(region: string, storageRegion: string): { valid: boolean; warning?: string } {
    const gdprRegions = ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'PT', 'IE', 'FI', 'SE', 'DK', 'NO', 'IS', 'LU', 'MT', 'CY', 'EE', 'LV', 'LT', 'PL', 'CZ', 'SK', 'HU', 'SI', 'HR', 'RO', 'BG', 'GB'];
    const euRegions = ['eu-west-1', 'eu-central-1', 'eu-north-1', 'eu-south-1'];
    
    if (gdprRegions.includes(region)) {
      if (!euRegions.some(euRegion => storageRegion.includes(euRegion))) {
        return {
          valid: false,
          warning: 'GDPR compliance requires data storage within EU regions'
        };
      }
    }
    
    return { valid: true };
  }

  // Cloud region proximity sorting
  sortCloudRegionsByProximity(regions: string[]): string[] {
    const regionProximity: Record<string, string[]> = {
      'US': ['us-east-1', 'us-west-2', 'us-west-1', 'ca-central-1', 'eu-west-1'],
      'CA': ['ca-central-1', 'us-east-1', 'us-west-2', 'us-west-1', 'eu-west-1'],
      'GB': ['eu-west-2', 'eu-west-1', 'eu-central-1', 'us-east-1', 'ap-southeast-1'],
      'DE': ['eu-central-1', 'eu-west-1', 'eu-west-2', 'eu-north-1', 'us-east-1'],
      'FR': ['eu-west-3', 'eu-west-1', 'eu-central-1', 'eu-west-2', 'us-east-1'],
      'JP': ['ap-northeast-1', 'ap-northeast-3', 'ap-southeast-1', 'ap-southeast-2', 'us-west-2'],
      'AU': ['ap-southeast-2', 'ap-southeast-1', 'ap-northeast-1', 'us-west-2', 'eu-west-1'],
      'IN': ['ap-south-1', 'ap-southeast-1', 'ap-northeast-1', 'eu-west-1', 'us-east-1'],
      'CN': ['cn-north-1', 'cn-northwest-1', 'ap-northeast-1', 'ap-southeast-1', 'us-west-2'],
      'BR': ['sa-east-1', 'us-east-1', 'us-west-2', 'eu-west-1', 'ap-southeast-1']
    };
    
    const userRegion = this.settings.region || 'US';
    const proximityOrder = regionProximity[userRegion] || regionProximity['US'];
    
    return regions.sort((a, b) => {
      const aIndex = proximityOrder.findIndex(region => a.includes(region));
      const bIndex = proximityOrder.findIndex(region => b.includes(region));
      
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      
      return aIndex - bIndex;
    });
  }

  generateOptimalBackupStrategy(moduleId: string, preferences: {
    timezone: string;
    businessHours?: { start: string; end: string };
    complianceRequirements?: string[];
  }): any {
    const isDatabase = moduleId.includes('postgresql') || moduleId.includes('mysql') || moduleId.includes('mongodb');
    const businessHours = preferences.businessHours || { start: '09:00', end: '17:00' };
    
    // Calculate optimal backup time (outside business hours)
    const optimalTime = this.calculateOptimalBackupTime(businessHours, preferences.timezone);
    
    // Determine frequency based on module type and compliance
    const frequency = this.determineOptimalFrequency(moduleId, preferences.complianceRequirements);
    
    // Set retention based on compliance requirements
    const retentionDays = this.calculateOptimalRetention(moduleId, preferences.complianceRequirements);
    
    return {
      scheduleType: 'simple',
      simpleFrequency: frequency,
      time: optimalTime,
      cronExpression: '0 2 * * *',
      intervalValue: 1,
      intervalUnit: 'hours',
      type: isDatabase ? 'incremental' : 'full',
      retentionDays,
      retentionPolicy: {
        type: 'simple',
        dailyRetention: 7,
        weeklyRetention: 4,
        monthlyRetention: 12,
        fullBackupRetention: 90,
        incrementalRetention: 30,
        legalHold: false,
        legalHoldUntil: null
      },
      storageType: 'local',
      storagePath: this.getOptimalStoragePath(moduleId),
      cloudProvider: '',
      cloudBucket: '',
      cloudRegion: this.getOptimalCloudRegion(),
      cloudAccessKey: '',
      cloudSecretKey: '',
      sftpHost: '',
      sftpPort: 22,
      sftpUser: '',
      sftpPath: '',
      sftpPassword: '',
      sftpKey: '',
      encryption: true,
      compression: true,
      compressionLevel: 6,
      bandwidthLimit: 0
    };
  }

  private calculateOptimalBackupTime(businessHours: { start: string; end: string }, timezone: string): string {
    const endHour = parseInt(businessHours.end.split(':')[0]);
    const optimalHour = endHour >= 18 ? 2 : endHour + 1;
    return `${optimalHour.toString().padStart(2, '0')}:00`;
  }

  private determineOptimalFrequency(moduleId: string, complianceRequirements?: string[]): string {
    if (complianceRequirements?.includes('GDPR') || complianceRequirements?.includes('SOX')) {
      return 'daily';
    }
    
    if (moduleId.includes('postgresql') || moduleId.includes('mysql') || moduleId.includes('mongodb')) {
      return 'daily';
    }
    
    return 'weekly';
  }

  private calculateOptimalRetention(moduleId: string, complianceRequirements?: string[]): number {
    if (complianceRequirements?.includes('SOX')) {
      return 2555; // 7 years
    }
    
    if (complianceRequirements?.includes('GDPR')) {
      return 1095; // 3 years
    }
    
    if (moduleId.includes('postgresql') || moduleId.includes('mysql') || moduleId.includes('mongodb')) {
      return 90;
    }
    
    return 30;
  }

  private getOptimalStoragePath(moduleId: string): string {
    const basePath = '/var/lib/backups';
    const moduleType = moduleId.split('-')[0] || 'general';
    return `${basePath}/${moduleType}`;
  }

  private getOptimalCloudRegion(): string {
    const regionMap: Record<string, string> = {
      'US': 'us-east-1',
      'CA': 'ca-central-1', 
      'GB': 'eu-west-2',
      'DE': 'eu-central-1',
      'FR': 'eu-west-3',
      'JP': 'ap-northeast-1',
      'AU': 'ap-southeast-2',
      'BR': 'sa-east-1'
    };
    
    return regionMap[this.settings.region || 'US'] || 'us-east-1';
  }
}